/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import {
  integerRegExp,
} from 'utils/patterns';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getPeriodosDeNominasDetalles,
  postPeriodoDeNominaDetalle,
  deletePeriodoDeNominaDetalle,
  patchPeriodoDeNominaDetalle,
  getPeriodoDeNominaDetalle,
} from 'api/nominas/periodos/periodosDeNominasDetalle';
import { checkToken } from 'api/utils';

export const periodoDeNominaDetallePermission = {
  permissionModel: 'periododenomina',
  catalogsModels: [],
  tabsModels: [],
};

const PeriodoDeNominaDetalle = ({
  permission,
  selected,
  estatusInactivo,
  estatusAutorizado,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPeriodosDeNominasDetalles({ periodo_de_nomina: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosDeNominasDetalles({ periodo_de_nomina: selected }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [selected]);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _periodo = await getPeriodoDeNominaDetalle(match.id);
    setTimeout(() => {
      if (_periodo) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_periodo));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchPeriodoDeNominaDetalle(key, { ...values, periodo_de_nomina: selected, numero_de_nomina: '1' })
        : postPeriodoDeNominaDetalle({ ...values, periodo_de_nomina: selected, numero_de_nomina: '1' })
      );
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePeriodoDeNominaDetalle(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 200,
    },
    {
      titleText: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
      width: 200,
    },
    {
      titleText: 'No. de Nómina',
      dataIndex: 'numero_de_nomina',
      key: 'numero_de_nomina',
      width: 200,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 200,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'No. de Días',
      dataIndex: 'no_de_dias',
      key: 'no_de_dias',
      width: 200,
    },
    {
      titleText: 'No. de Días Naturales',
      key: 'no_de_dias_naturales',
      width: 200,
      render: (_, record) => {
        if (record.fecha_inicial && record.fecha_final) {
          return record.fecha_final.diff(record.fecha_inicial, 'days') + 1;
        }
        return '-';
      },
    },
    {
      titleText: 'Cierre de Mes',
      dataIndex: 'cierre_mes',
      key: 'cierre_mes',
      width: 200,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'No. de Periodos',
      dataIndex: 'no_de_periodos',
      key: 'no_de_periodos',
      width: 200,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    estatus: [requiredRule],
    dias: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
    ],
    fecha_inicial: [
      requiredRule,
      {
        validator: async () => {
          const { fecha_inicial, fecha_final } = form.getFieldsValue();
          if (fecha_inicial && fecha_final && fecha_inicial.diff(fecha_final, 'days') >= 0) {
            throw new Error('La Fecha Inicial debe ser menor que la Fecha Final');
          }
        },
      },
    ],
    fecha_final: [
      requiredRule,
      {
        validator: async () => {
          const { fecha_inicial, fecha_final } = form.getFieldsValue();
          if (fecha_inicial && fecha_final && fecha_final.diff(fecha_inicial, 'days') <= 0) {
            throw new Error('La Fecha Final debe ser mayor que la Fecha Inicial');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableAdd={estatusAutorizado || estatusInactivo}
          disableDelete={estatusAutorizado || estatusInactivo}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={selectedRowKeys.length ? 'Editar Detalle de Periodo de Nómina' : 'Agregar Detalle de Periodo de Nómina'}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="75%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="fecha_inicial"
                  label="Fecha de Inicio"
                  rules={rules.fecha_inicial}
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD/MM/YYYY"
                    disabled={estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="fecha_final"
                  label="Fecha Final"
                  rules={rules.fecha_final}
                  hasFeedback
                >
                  <DatePicker
                    allowClear
                    format="DD/MM/YYYY"
                    disabled={estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="no_de_dias"
                  label="No. de Días"
                  rules={rules.dias}
                  disabled={estatusAutorizado || estatusInactivo}
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled={!selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Detalle de Periodo de Nómina"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PeriodoDeNominaDetalle;
